import { BusinessCase, FREIGHT_TYPE, PARTNER_ORGANIZATION } from '@schuettflix/interfaces';
import { isWasteProduct } from '@/shared/domain/product/isWasteProduct.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { isWasteFreightType } from '@/shared/domain/product/isWasteFreightType.ts';
import { LocationSource } from '@/clients/location/useLocations.ts';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';

export type GenerateLocationSourcesParams = {
    businessCase: BusinessCase;
    locationType: LocationType;
    supplierOrganizationId?: number;
    carrierOrganizationId?: PARTNER_ORGANIZATION;
    constructionProjectId: string;
    customerOrganizationId: number;
    freightType?: FREIGHT_TYPE;
};

type GenerateLocationSourcesReturn = {
    locationSources: Array<LocationSource>;
    defaultOrganizationId?: number;
};

export function generateLocationSources({
    businessCase,
    locationType,
    constructionProjectId,
    carrierOrganizationId,
    supplierOrganizationId,
    customerOrganizationId,
    freightType,
}: GenerateLocationSourcesParams): GenerateLocationSourcesReturn {
    const isAWasteCase = isWasteProduct(businessCase.productType);
    const isATransportOnlyCase = isTransportProduct(businessCase.productType);
    const isWasteTransport = isWasteFreightType(freightType);

    switch (businessCase.pricingStrategy) {
        case 'STANDARD': {
            if (businessCase.isStandalone) {
                if (isATransportOnlyCase) {
                    if (carrierOrganizationId === 'TRANSPORT_EXCHANGE') {
                        return {
                            defaultOrganizationId: customerOrganizationId,
                            locationSources: [
                                {
                                    type: 'CONSTRUCTION_PROJECT',
                                    id: constructionProjectId,
                                    organizationId: customerOrganizationId,
                                },
                            ],
                        };
                    }

                    if (locationType === 'LOADING') {
                        if (isWasteTransport) {
                            return {
                                defaultOrganizationId: customerOrganizationId,
                                locationSources: [
                                    {
                                        type: 'CONSTRUCTION_PROJECT',
                                        id: constructionProjectId,
                                        organizationId: customerOrganizationId,
                                    },
                                ],
                            };
                        }

                        if (carrierOrganizationId) {
                            return {
                                defaultOrganizationId: carrierOrganizationId,
                                locationSources: [{ type: 'ORGANIZATION', id: carrierOrganizationId }],
                            };
                        }
                    }

                    if (locationType === 'UNLOADING') {
                        if (!isWasteTransport) {
                            return {
                                defaultOrganizationId: customerOrganizationId,
                                locationSources: [
                                    {
                                        type: 'CONSTRUCTION_PROJECT',
                                        id: constructionProjectId,
                                        organizationId: customerOrganizationId,
                                    },
                                ],
                            };
                        }

                        if (carrierOrganizationId) {
                            return {
                                defaultOrganizationId: carrierOrganizationId,
                                locationSources: [{ type: 'ORGANIZATION', id: carrierOrganizationId }],
                            };
                        }
                    }
                }
            } else {
                if (locationType === 'LOADING') {
                    if (isAWasteCase) {
                        return {
                            defaultOrganizationId: customerOrganizationId,
                            locationSources: [
                                {
                                    type: 'CONSTRUCTION_PROJECT',
                                    id: constructionProjectId,
                                    organizationId: customerOrganizationId,
                                },
                            ],
                        };
                    }

                    if (supplierOrganizationId) {
                        return {
                            defaultOrganizationId: supplierOrganizationId,
                            locationSources: [{ type: 'ORGANIZATION', id: supplierOrganizationId }],
                        };
                    }
                }

                if (locationType === 'UNLOADING') {
                    if (!isAWasteCase) {
                        return {
                            defaultOrganizationId: customerOrganizationId,
                            locationSources: [
                                {
                                    type: 'CONSTRUCTION_PROJECT',
                                    id: constructionProjectId,
                                    organizationId: customerOrganizationId,
                                },
                            ],
                        };
                    }

                    if (supplierOrganizationId) {
                        return {
                            defaultOrganizationId: supplierOrganizationId,
                            locationSources: [{ type: 'ORGANIZATION', id: supplierOrganizationId }],
                        };
                    }
                }
            }
            return { locationSources: [], defaultOrganizationId: customerOrganizationId };
        }
        case 'MERCHANT': {
            if (locationType === 'LOADING') {
                if (isAWasteCase) {
                    return {
                        defaultOrganizationId: customerOrganizationId,
                        locationSources: [
                            {
                                type: 'CONSTRUCTION_PROJECT',
                                id: constructionProjectId,
                                organizationId: customerOrganizationId,
                            },
                        ],
                    };
                }

                // In merchant, the partnerOrganizationId is both a carrier and supplier
                // adding a fallback to the supplierOrganizationId in case carrier was not set.
                const partnerOrganizationId =
                    carrierOrganizationId && carrierOrganizationId !== 'TRANSPORT_EXCHANGE'
                        ? carrierOrganizationId
                        : supplierOrganizationId;

                if (partnerOrganizationId) {
                    return {
                        defaultOrganizationId: partnerOrganizationId,
                        locationSources: [{ type: 'ORGANIZATION', id: partnerOrganizationId }],
                    };
                }
            }

            if (locationType === 'UNLOADING') {
                if (!isAWasteCase) {
                    return {
                        defaultOrganizationId: customerOrganizationId,
                        locationSources: [
                            {
                                type: 'CONSTRUCTION_PROJECT',
                                id: constructionProjectId,
                                organizationId: customerOrganizationId,
                            },
                        ],
                    };
                }

                // In merchant, the partnerOrganizationId is both a carrier and supplier
                // adding a fallback to the supplierOrganizationId in case carrier was not set.
                const partnerOrganizationId =
                    carrierOrganizationId && carrierOrganizationId !== 'TRANSPORT_EXCHANGE'
                        ? carrierOrganizationId
                        : supplierOrganizationId;

                if (partnerOrganizationId) {
                    return {
                        defaultOrganizationId: partnerOrganizationId,
                        locationSources: [{ type: 'ORGANIZATION', id: partnerOrganizationId }],
                    };
                }
            }

            return { locationSources: [], defaultOrganizationId: customerOrganizationId };
        }
        case 'FRANCO': {
            if (locationType === 'LOADING') {
                if (isAWasteCase) {
                    return {
                        defaultOrganizationId: customerOrganizationId,
                        locationSources: [
                            {
                                type: 'CONSTRUCTION_PROJECT',
                                id: constructionProjectId,
                                organizationId: customerOrganizationId,
                            },
                        ],
                    };
                }

                if (supplierOrganizationId) {
                    return {
                        defaultOrganizationId: supplierOrganizationId,
                        locationSources: [{ type: 'ORGANIZATION', id: supplierOrganizationId }],
                    };
                }
            }

            if (locationType === 'UNLOADING') {
                if (!isAWasteCase) {
                    return {
                        defaultOrganizationId: customerOrganizationId,
                        locationSources: [
                            {
                                type: 'CONSTRUCTION_PROJECT',
                                id: constructionProjectId,
                                organizationId: customerOrganizationId,
                            },
                        ],
                    };
                }

                if (supplierOrganizationId) {
                    return {
                        defaultOrganizationId: supplierOrganizationId,
                        locationSources: [{ type: 'ORGANIZATION', id: supplierOrganizationId }],
                    };
                }
            }

            return { locationSources: [], defaultOrganizationId: customerOrganizationId };
        }
        default: {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`${businessCase.pricingStrategy} value is not handled`);
        }
    }
}
